/* eslint-disable jsx-a11y/media-has-caption */
import { alpha, Box, Button, Paper, useTheme } from '@material-ui/core';
import CustomAvatar from '@src/components/Common/CustomAvatar';
import IconWrapper from '@src/components/Common/IconWrapper';
import LinearProgress from '@src/components/Common/LinearProgress';
import IconButton from '@src/components/UI/IconButton';
import Typography from '@src/components/UI/Typography';
import useCallAgentPermissionFlags from '@src/hooks/call/useCallAgentPermissionFlags';
import useCallDirectionFlags from '@src/hooks/call/useCallDirectionFlags';
import useChatPickupAsync from '@src/hooks/useChatPickupAsync';
import usePreventMultipleClicks from '@src/hooks/usePreventMultipleClicks';
import { useCallMedia } from '@src/logic/call';
import { decrementTotalCallsOnHold } from '@src/reducers/chats';
import CallEndIcon from '@src/resources/icons/CallEndIcon';
import CallStartIcon from '@src/resources/icons/CallStartIcon';
import MicrophoneIcon from '@src/resources/icons/MicrophoneIcon';
import MicrophoneOffIcon from '@src/resources/icons/MicrophoneOffIcon';
import RecordIcon from '@src/resources/icons/RecordIcon';
import { selectActiveCallId } from '@src/selectors/calls';
import { chatByIdSelector } from '@src/selectors/chats';
import { getUserId } from '@src/selectors/user';
import { Call, CallStatus, CallUser } from '@src/types/calls';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import CallSectionTimer from './CallSectionTimer';

const CallSection: React.FC<{ call: Call }> = ({ call }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const callMedia = useCallMedia(call);
  const pickupChatAsync = useChatPickupAsync();
  const { isEnabled } = useCallAgentPermissionFlags();

  const agentId = useSelector(getUserId);
  const activeCallId = useSelector(selectActiveCallId);

  const chatInCall = useSelector(chatByIdSelector(call.chatId));
  const isActiveThisCall = call?.id === activeCallId;

  const callDirectionFlags = useCallDirectionFlags(call.direction);

  const callUserName = useMemo(() => {
    const user = (
      callDirectionFlags.isCallIncoming ? call.caller : call.callee
    ) as CallUser;

    return user.name ?? '';
  }, [call.callee, call.caller, callDirectionFlags.isCallIncoming]);

  // The pickup / counter update runs inside startCall, after it claimed the answer, so
  // a click it refuses (the other Answer button won) does neither.
  const onStartCall = () =>
    callMedia.startCall(async () => {
      const isChatOnHoldSide = !!chatInCall.assignedTo?.id;
      if (!isChatOnHoldSide) await pickupChatAsync(call.chatId, agentId);
      else dispatch(decrementTotalCallsOnHold());
    });

  const safeStartCall = usePreventMultipleClicks(onStartCall, {
    blockTimeMs: 2000,
  });

  const safeEndCall = usePreventMultipleClicks(() => callMedia.endCall(), {
    blockTimeMs: 2000,
  });

  useEffect(() => {
    if (!call?.startedAt) return;

    const hasWaitingStatus = [
      CallStatus.WAITING_AGENT,
      CallStatus.WAITING_ANSWER,
    ].includes(call.status);

    if (!hasWaitingStatus) return;

    const isOlderThanOneMinute = () => {
      const oneMinuteAgo = dayjs().subtract(1, 'minute');
      return dayjs(call.startedAt).isBefore(oneMinuteAgo);
    };

    if (isOlderThanOneMinute()) callMedia.endCall(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.startedAt]);

  return (
    <Box
      py={3}
      px={2}
      mt={1}
      display="flex"
      justifyContent="space-between"
      component={Paper}
      borderRadius={8}
    >
      <Box display="flex" gridGap={6} alignItems="center">
        <CustomAvatar avatarSrc="" size="lg" />
        <Box>
          {callUserName && (
            <Typography fontWeight="bold" style={{ letterSpacing: '0.1px' }}>
              {callUserName}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        width={220}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gridGap={6}
      >
        {!isActiveThisCall ? (
          <LinearProgress
            color={theme.b2chatgreen['300']}
            variant="indeterminate"
          />
        ) : (
          <Typography
            fontWeight="bold"
            variant="body-l"
            style={{ lineHeight: 1 }}
          >
            <CallSectionTimer startTime={call.startedAt} />
          </Typography>
        )}

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gridGap={5}
        >
          <IconWrapper color={theme.b2chatgreen['300']} fontSize={18}>
            <RecordIcon />
          </IconWrapper>

          {isActiveThisCall
            ? formatMessage({ id: 'Calling.ActiveCallLbl' })
            : formatMessage({
                id: callDirectionFlags.isCallOutgoing
                  ? 'Calling.OutgoingCallLbl'
                  : 'Calling.IncomingCallLbl',
              })}
        </Box>
      </Box>
      <Box display="flex" gridGap={10}>
        {isActiveThisCall && (
          <Button
            color="primary"
            style={{
              minWidth: 45,
              width: 45,
              height: 45,
              borderRadius: 25,
              backgroundColor: callMedia.muted
                ? alpha(theme.palette.primary.main, 0.6)
                : theme.palette.primary.main,
              color: callMedia.muted
                ? theme.palette.primary.main
                : alpha(theme.palette.primary.main, 0.5),
            }}
            onClick={
              callMedia.muted ? callMedia.unmuteCall : callMedia.muteCall
            }
          >
            <IconWrapper fontSize={22} color="#FFF">
              {callMedia.muted ? <MicrophoneOffIcon /> : <MicrophoneIcon />}
            </IconWrapper>
          </Button>
        )}
        <IconButton
          color="primary"
          style={{
            height: 45,
            width: isActiveThisCall ? 110 : 'auto',
            borderRadius: 25,
            backgroundColor: theme.b2chatred['500'],
          }}
          onClick={safeEndCall}
          loading={callMedia.ending}
        >
          <Box gridGap={8} display="flex" alignItems="center" color="#FFF">
            <IconWrapper fontSize={22}>
              <CallEndIcon />
            </IconWrapper>

            {isActiveThisCall && (
              <Box
                style={{ fontSize: '.93rem', fontWeight: 500, lineHeight: 0 }}
              >
                {formatMessage({ id: 'Calling.EndCallLbl' })}
              </Box>
            )}
          </Box>
        </IconButton>
        {!isActiveThisCall && !callDirectionFlags.isCallOutgoing && (
          <IconButton
            color="primary"
            style={{
              height: 45,
              width: 110,
              borderRadius: 25,
              backgroundColor: theme.b2chatgreen['300'],
            }}
            onClick={safeStartCall}
            loading={callMedia.starting}
            disabled={!isEnabled}
          >
            <Box gridGap={8} display="flex" alignItems="center" color="#FFF">
              <IconWrapper fontSize={22} color="inherit">
                <CallStartIcon />
              </IconWrapper>

              <Box
                style={{ fontSize: '.93rem', fontWeight: 500, lineHeight: 0 }}
              >
                {formatMessage({ id: 'Calling.StartCallLbl' })}
              </Box>
            </Box>
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default CallSection;
